import React, { useMemo } from 'react';
import LazyLoad from 'react-lazyload';
import { number } from 'prop-types';
import Button from '@alltrails/shared/denali/components/Button';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { DESKTOP_BREAKPOINT } from 'utils/constants/breakpoints';
import { defineMessages, useIntl } from 'react-intl';
import useUser from 'hooks/useUser';
import useFeatures from 'hooks/useFeatures';
import logHomepageProUpsellBlockButtonClicked from '@alltrails/analytics/events/logHomepageProUpsellBlockButtonClicked';
import FeatureRow from './FeatureRow';
import * as styles from './styles/styles.module.scss';
import logoPro from './logo_pro.png';
import proHeader from './pro_header.png';

const BG_IMAGE_BASE = 'https://cdn-assets.alltrails.com/assets/images/homepage/pro_upsell/';

const messages = defineMessages({
  free: {
    defaultMessage: 'free'
  },
  stayOnTrack: {
    defaultMessage: 'Stay on track while offline with Pro'
  },
  features: {
    defaultMessage: 'features'
  },
  featureFavorite: {
    defaultMessage: 'Favorite trails and create lists'
  },
  featureLog: {
    defaultMessage: 'Log your activities'
  },
  realTimeOverlays: {
    defaultMessage: 'See real-time overlays'
  },
  featureOffline: {
    defaultMessage: 'Download offline maps'
  },
  featurePrint: {
    defaultMessage: 'Print maps for backup'
  },
  featureOffRoute: {
    defaultMessage: 'Get off-route notifications'
  },
  tryProFree: {
    defaultMessage: 'Try Pro for Free'
  },
  buyPro: {
    defaultMessage: 'Buy Pro'
  }
});

type ProUpsellProps = {
  windowWidth: number;
};

const ProUpsell = ({ windowWidth }: ProUpsellProps): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const features = useFeatures();
  const intl = useIntl();

  const featuresContainerStyle = useMemo(() => {
    const isDesktop = windowWidth && windowWidth >= DESKTOP_BREAKPOINT;

    if (!isDesktop) {
      return {};
    }

    const fileName = `bg_desktop_${languageRegionCode}.jpg`;
    const backgroundImageUrl = `${BG_IMAGE_BASE}${fileName}`;

    return {
      backgroundImage: `url(${backgroundImageUrl})`
    };
  }, [windowWidth]);

  const upperContainerStyle = {
    backgroundImage: `url(${BG_IMAGE_BASE}bg_mobile_${languageRegionCode}.jpg)`
  };

  return (
    <div className={styles.container}>
      <div style={upperContainerStyle} className={styles.upperContainer} />
      <div style={featuresContainerStyle} className={styles.featuresContainer}>
        <LazyLoad>
          <img className={styles.logoProImage} src={logoPro} alt="" />
        </LazyLoad>
        <h2 className={styles.title}>{intl.formatMessage(messages.stayOnTrack)}</h2>
        <div className={styles.headerRow}>
          <div className={[styles.tableHeader, styles.headerRowFeature].join(' ')}>{intl.formatMessage(messages.features)}</div>
          <div className={[styles.tableHeader, styles.headerRowFreePro].join(' ')}>{intl.formatMessage(messages.free)}</div>
          <div className={styles.headerRowFreePro}>
            <LazyLoad>
              <img src={proHeader} className={styles.headerRowProIcon} alt="" />
            </LazyLoad>
          </div>
        </div>
        <FeatureRow free tint text={intl.formatMessage(messages.featureFavorite)} />
        <FeatureRow free tint={false} text={intl.formatMessage(messages.featureLog)} />
        <FeatureRow free={false} tint text={intl.formatMessage(messages.featureOffline)} />
        <FeatureRow free={false} tint={false} text={intl.formatMessage(messages.realTimeOverlays)} />
        <FeatureRow free={false} tint text={intl.formatMessage(messages.featurePrint)} />
        <FeatureRow free={false} tint={false} text={intl.formatMessage(messages.featureOffRoute)} />
        <div className={styles.buyLink}>
          <Button
            text={
              (!user || user.isPromoEligible) && features.includes('seven_day_trial')
                ? intl.formatMessage(messages.tryProFree)
                : intl.formatMessage(messages.buyPro)
            }
            linkInfo={{ href: LanguageSupportUtil.wrapUrlSafe('/pro?ref=homepage_pro_proupsell', languageRegionCode) }}
            onClick={logHomepageProUpsellBlockButtonClicked}
            testId="pro-upsell-try-or-buy-pro"
            variant="primary"
          />
        </div>
      </div>
    </div>
  );
};

ProUpsell.propTypes = {
  windowWidth: number
};

ProUpsell.defaultProps = {
  windowWidth: null
};

export default ProUpsell;
